import { type Block, parseMarkdown, plainText } from "./markdown-parse.ts";

// Stable heading keys (wiki-collaboration D17): a heading is known by its text, slugged, and
// its occurrence among headings with the same slug, so a key survives edits elsewhere on the
// page. The reading view's anchors, its table of contents and page-thread anchors share it.

/** A heading's key: its slug and its occurrence (0 for the first) among same-slug headings. */
export interface HeadingAnchor {
  slug: string;
  occurrence: number;
  /** The heading's text, markup dropped. */
  text: string;
  /** The heading's index in the parsed blocks. */
  block: number;
}

/** The longest slug kept, in code points; a longer heading is cut, which keeps its key stable. */
export const HEADING_SLUG_MAX = 120;

/** Lower case letters and digits of any script, other runs as one `-`; never empty. */
export function headingSlug(text: string): string {
  const full = text
    .normalize("NFKC")
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, "-")
    .replace(/^-+|-+$/g, "");
  // Cut by code points, never inside a surrogate pair.
  const slug = Array.from(full).slice(0, HEADING_SLUG_MAX).join("").replace(/-+$/, "");
  return slug || "section";
}

/** The keys of parsed `blocks`' headings, in order. */
export function headingKeys(blocks: readonly Block[]): HeadingAnchor[] {
  const seen = new Map<string, number>();
  return blocks.flatMap((block, index) => {
    if (block.type !== "heading") {
      return [];
    }
    const text = plainText(block.content).trim();
    const slug = headingSlug(text);
    const occurrence = seen.get(slug) ?? 0;
    seen.set(slug, occurrence + 1);
    return [{ slug, occurrence, text, block: index }];
  });
}

/** The keys of a page's headings, in order. */
export function headingAnchors(markdown: string): HeadingAnchor[] {
  return headingKeys(parseMarkdown(markdown));
}

/** A heading's id fragment: the slug, then `--2`, `--3`… for later same-slug headings. */
export const headingFragment = (anchor: Pick<HeadingAnchor, "slug" | "occurrence">) =>
  anchor.occurrence === 0 ? anchor.slug : `${anchor.slug}--${anchor.occurrence + 1}`;
